import * as React from 'react';
import { useCallback, useState } from 'react';
import { Stack } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Unstable_Grid2';
import { DynamicQuestionnaire } from 'benchmarking-domain/dynamic-questionnaire';
import { PermissionsHelper } from 'benchmarking-domain/permissions-helper';
import Project from 'benchmarking-domain/project';
import ProjectSection from 'benchmarking-domain/project-section';
import { QuestionnaireSection } from 'benchmarking-domain/questionnaire';

import { useOrganisation } from '../../../hooks/use-organisation';
import { useUser } from '../../../hooks/use-user';
import { UserSelect } from '../../core/shared/user-select';
import DynamicQuestionnaireCreateForm from './dynamic-questionnaire-create-form';
import { ProjectSectionKpiButton } from './project-section-kpi-button';

export interface QuestionnaireSectionDetailsProps {
  projectSection: ProjectSection;
  questionnaireSection: QuestionnaireSection;
  project: Project;
  handleProjectSectionChange: (data: ProjectSection, submit?: boolean) => void;
  dynamicQuestionnaireDetails?: { dynamicQuestionnaire?: DynamicQuestionnaire; maxOccurrence?: number };
}

export function QuestionnaireSectionDetails({
  projectSection,
  questionnaireSection,
  project,
  handleProjectSectionChange,
  dynamicQuestionnaireDetails,
}: QuestionnaireSectionDetailsProps): React.JSX.Element {
  const { organisationId } = useOrganisation();
  const { user } = useUser();

  const [prevProjectSection, setPrevProjectSection] = useState<ProjectSection | undefined>(projectSection);

  const [managers, setManagers] = useState<string[]>(projectSection.managers ?? []);

  if (prevProjectSection?.id !== projectSection.id) {
    setPrevProjectSection(projectSection);
    setManagers(projectSection.managers ?? []);
  }

  const handleDataChange = (newManagers: string[]): void => {
    setManagers(newManagers);
    handleProjectSectionChange({ ...projectSection, id: '', managers: newManagers }, false);
  };

  const handleAddDynamicItem = useCallback(
    async (newItemName: string): Promise<{ errorMessage?: string }> => {
      if (!dynamicQuestionnaireDetails || !dynamicQuestionnaireDetails.dynamicQuestionnaire) {
        return { errorMessage: 'The section does not support dynamic items.' };
      }

      if (
        (projectSection?.dynamicItems?.length ?? 0) >= (dynamicQuestionnaireDetails.maxOccurrence ?? Number.MAX_VALUE)
      ) {
        return { errorMessage: 'Maximum number of items reached.' };
      }

      if ((projectSection?.dynamicItems?.indexOf(newItemName) ?? -1) > -1) {
        return { errorMessage: 'This name is already used.' };
      }

      const dynamicItemCopy = projectSection?.dynamicItems ? [...projectSection.dynamicItems] : [];
      dynamicItemCopy.push(newItemName);

      handleProjectSectionChange({ ...projectSection, dynamicItems: dynamicItemCopy }, true);
      return {};
    },
    [dynamicQuestionnaireDetails, handleProjectSectionChange, projectSection]
  );

  return (
    <>
      <Stack spacing={1} direction={'row'} marginBottom={3}>
        <ProjectSectionKpiButton
          questionnaireSection={questionnaireSection}
          project={project}
          projectSections={[projectSection]}
        />
        {dynamicQuestionnaireDetails && dynamicQuestionnaireDetails.dynamicQuestionnaire && (
          <DynamicQuestionnaireCreateForm
            dynamicQuestionnaireDetails={dynamicQuestionnaireDetails}
            onSubmit={handleAddDynamicItem}
          />
        )}
      </Stack>

      <Grid container spacing={3}>
        <Grid md={6} xs={12}>
          <FormControl fullWidth required>
            <UserSelect
              id="managers-filled"
              organisationId={organisationId}
              label="Responsible users"
              value={managers}
              onChange={handleDataChange}
              disabled={!PermissionsHelper.hasUserAtLeastRole('projectManager', user, project)}
            />
          </FormControl>
        </Grid>
      </Grid>
    </>
  );
}
